import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useState } from 'react';
import type { Controller } from '@/types';
import { isSwatidController } from '@/utils/controllerType';
import type { ApiController } from '@/types/api';
import { SwatidControllerDetail } from '@/components/swatid/SwatidControllerDetail';
import { cn } from '@/lib/utils';
import {
  Cpu,
  Wifi,
  WifiOff,
  Battery,
  MapPin,
  Calendar,
  Radio,
  Droplets,
  History,
  Terminal,
  Activity,
  Settings,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { CommandHistoryTable } from './CommandHistoryTable';
import { BatteryHistoryChart } from './BatteryHistoryChart';
import { ValveTimelineChart } from './ValveTimelineChart';
import { LocalModeSection } from './LocalModeSection';
import { LocalSchedulesSection } from './LocalSchedulesSection';

interface ControllerDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controller: Controller | null;
  companyName?: string;
  /** Datos API crudos para detectar SWATID */
  apiController?: ApiController | null;
}

export function ControllerDetailDialog({
  open,
  onOpenChange,
  controller,
  companyName,
  apiController,
}: ControllerDetailDialogProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!controller) return null;

  const swatid =
    apiController && isSwatidController(apiController)
      ? apiController
      : isSwatidController({
          model: controller.model as ApiController['model'],
          communicationType: controller.communicationType as ApiController['communicationType'],
        })
        ? ({ model: 'SWATID_16', communicationType: 'mqtt_swatid' } as ApiController)
        : null;

  if (swatid || controller.model === 'SWATID_16' || controller.communicationType === 'mqtt_swatid') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
            <DialogTitle className="font-display text-xl">{controller.name}</DialogTitle>
            <DialogDescription className="font-mono">
              SWATID-16 · {controller.serialNumber || 'Sin serie'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <SwatidControllerDetail controller={controller} companyName={companyName} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-success';
      case 'offline':
        return 'bg-destructive';
      case 'warning':
        return 'bg-warning';
      default:
        return 'bg-muted';
    }
  };

  const getBatteryColor = (battery?: number) => {
    if (!battery) return 'text-muted-foreground';
    if (battery > 50) return 'text-success';
    if (battery > 20) return 'text-warning';
    return 'text-destructive';
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('es-ES', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  // Helpers para estados pendientes
  const hasPendingModeChange = (): boolean => {
    return controller.pendingOperationMode !== null && controller.pendingOperationMode !== undefined;
  };

  const getPendingDuration = (): number => {
    if (!controller.pendingModeAt) return 0;
    return Date.now() - new Date(controller.pendingModeAt).getTime();
  };

  const isPendingTooLong = (): boolean => {
    return getPendingDuration() > 5 * 60 * 1000; // 5 minutos
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'p-3 rounded-xl',
                controller.status === 'online' ? 'bg-success/10' : 'bg-muted'
              )}
            >
              <Cpu
                className={cn(
                  'w-6 h-6',
                  controller.status === 'online' ? 'text-success' : 'text-muted-foreground'
                )}
              />
            </div>
            <div>
              <DialogTitle className="font-display text-xl">{controller.name}</DialogTitle>
              <DialogDescription className="font-mono">{controller.model}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Estado de Cambio Pendiente */}
        {hasPendingModeChange() && (
          <div className="px-6 py-4 border-b bg-amber-500/5">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                Cambio de Modo Pendiente
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Modo Actual</span>
                  <Badge variant="outline" className="capitalize">
                    {controller.operationMode}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Cambiando a</span>
                  <Badge variant="outline" className="border-amber-500/50 text-amber-700 capitalize">
                    {controller.pendingOperationMode}
                  </Badge>
                </div>
                {controller.pendingModeAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Solicitado hace</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(controller.pendingModeAt), { addSuffix: true, locale: es })}
                    </span>
                  </div>
                )}
                {isPendingTooLong() && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      El cambio está tardando más de lo esperado. Verifique la conexión del dispositivo.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="px-6 py-3 flex-shrink-0 border-b bg-muted/30">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="overview" className="gap-2">
                <Cpu className="w-4 h-4" />
                Información
              </TabsTrigger>
              <TabsTrigger value="commands" className="gap-2">
                <Terminal className="w-4 h-4" />
                Comandos
              </TabsTrigger>
              <TabsTrigger value="battery" className="gap-2">
                <Battery className="w-4 h-4" />
                Batería
              </TabsTrigger>
              <TabsTrigger value="timeline" className="gap-2">
                <Activity className="w-4 h-4" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="local-mode" className="gap-2">
                <Settings className="w-4 h-4" />
                Modo Local
              </TabsTrigger>
              <TabsTrigger value="local-schedules" className="gap-2">
                <Clock className="w-4 h-4" />
                Programaciones
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {/* Pestañas con scroll compartido: overview, commands, battery, local-mode */}
            <div className={cn(
              "flex-1 overflow-y-auto overflow-x-hidden px-6 py-4",
              (activeTab === 'local-schedules' || activeTab === 'timeline') && "hidden"
            )}>
              <TabsContent value="overview" className="m-0 space-y-6">
          {/* Status Section */}
          <div className="flex items-center gap-3 flex-wrap">
            <Badge
              variant="outline"
              className={cn(
                'gap-1.5 px-3 py-1',
                controller.status === 'online' && 'border-success/50 text-success',
                controller.status === 'offline' && 'border-destructive/50 text-destructive'
              )}
            >
              {controller.status === 'online' ? (
                <Wifi className="w-3.5 h-3.5" />
              ) : (
                <WifiOff className="w-3.5 h-3.5" />
              )}
              {controller.status.charAt(0).toUpperCase() + controller.status.slice(1)}
            </Badge>
            <Badge variant="secondary" className="gap-1.5 px-3 py-1">
              <Radio className="w-3.5 h-3.5" />
              {controller.communicationType.toUpperCase()}
            </Badge>
            {controller.battery && (
              <Badge variant="outline" className={cn('gap-1.5 px-3 py-1', getBatteryColor(controller.battery))}>
                <Battery className="w-3.5 h-3.5" />
                {controller.battery}%
              </Badge>
            )}
          </div>

          <Separator />

          {/* Device Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">DevEUI</p>
              <p className="font-mono text-sm">{controller.deviceEUI}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Jardín</p>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-sm">{companyName || 'Sin asignar'}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Última conexión</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-sm">{formatDate(controller.lastSeen)}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Creado</p>
              <p className="text-sm">{formatDate(controller.createdAt)}</p>
            </div>
          </div>

          <Separator />

          {/* Valves Section */}
          <div>
            <h4 className="font-display font-semibold mb-3 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-primary" />
              Válvulas ({controller.valves.length})
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {controller.valves.map((valve) => (
                <div
                  key={valve.id}
                  className={cn(
                    'p-3 rounded-lg border',
                    valve.status === 'open' ? 'border-success/30 bg-success/5' : 'border-border bg-muted/30'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{valve.name}</span>
                    <Badge
                      variant={valve.status === 'open' ? 'default' : 'secondary'}
                      className={cn(
                        'text-xs',
                        valve.status === 'open' && 'bg-success hover:bg-success/90'
                      )}
                    >
                      {valve.status === 'open' ? 'Abierta' : 'Cerrada'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Canal {valve.channel}</p>
                </div>
              ))}
            </div>
          </div>
            </TabsContent>

              <TabsContent value="commands" className="m-0">
                <CommandHistoryTable controllerId={controller.id} controllerName={controller.name} />
              </TabsContent>

              <TabsContent value="battery" className="m-0">
                <BatteryHistoryChart controllerId={controller.id} controllerName={controller.name} />
              </TabsContent>

              <TabsContent value="local-mode" className="m-0">
                <LocalModeSection controller={controller} />
              </TabsContent>
            </div>
            
            {/* Timeline con scroll propio */}
            <TabsContent 
              value="timeline" 
              className={cn(
                "m-0 px-6 py-4 h-full flex flex-col min-h-0 overflow-hidden", 
                activeTab !== 'timeline' && "hidden"
              )}
            >
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <ValveTimelineChart 
                  controllerId={controller.id} 
                  controllerName={controller.name}
                  localMode={controller.localMode}
                  operationMode={controller.operationMode}
                />
              </div>
            </TabsContent>
            
            {/* Local Schedules con scroll propio */}
            <TabsContent 
              value="local-schedules" 
              className={cn(
                "m-0 p-0 h-full flex flex-col min-h-0", 
                activeTab !== 'local-schedules' && "hidden"
              )}
            >
              <LocalSchedulesSection
                controllerId={controller.id}
                controllerName={controller.name}
                localMode={controller.localMode}
                protocolVersion={controller.protocolVersion || 'v2'}
              />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
